<template>
  <div
    :class="DS_TOKEN_INPUT_CLASSES.Root"
    role="group"
    :aria-label="props.ariaLabel"
    data-cy="ds-token-input"
  >
    <DSBaseInput
      :id="props.id"
      ref="baseInput"
      type="text"
      :model-value="props.modelValue"
      :name="props.name"
      :disabled="props.disabled"
      :readonly="props.readonly"
      :has-error="props.hasError"
      :prefix="props.prefix"
      :suffix="props.suffix"
      :placeholder="placeholder"
      :autocomplete="props.autocomplete"
      :clearable="props.clearable && !isInactive"
      :force-show-clear="tokens.length > 0"
      wrap-content
      :role="props.role"
      :aria-invalid="props.ariaInvalid"
      :aria-controls="props.ariaControls"
      :aria-describedby="props.ariaDescribedby"
      :aria-expanded="props.ariaExpanded"
      :aria-activedescendant="props.ariaActivedescendant"
      :aria-autocomplete="props.ariaAutocomplete"
      :data-cy-suffix="props.dataCySuffix"
      @update:model-value="emit('update:modelValue', String($event))"
      @clear="emit('clear')"
      @click="onFieldClick"
      @focus="emit('focus', $event)"
      @blur="onBlur"
    >
      <template #value="{ inputAttrs, setInputRef, onInput }">
        <DSTokenInputChips
          :tokens="tokens"
          :id-prefix="idPrefix"
          :selected-index="selectedIndex"
          :disabled="isInactive"
          @open="actions.open"
        />
        <DSTokenInputDraft
          ref="draft"
          :input-attrs="inputAttrs"
          :set-input-ref="setInputRef"
          :order="2 * caret"
          :aria-describedby="draftDescribedby"
          @input="onDraftInput($event, onInput)"
          @keydown="onDraftKeydown"
          @keyup="emit('keyup', $event)"
          @click="emit('click', $event)"
          @select="emit('select', $event)"
        />
      </template>
    </DSBaseInput>

    <span
      :class="DS_TOKEN_INPUT_CLASSES.LiveRegion"
      aria-live="polite"
      data-cy="ds-token-input__live-region"
      >{{ announcement }}</span
    >

    <DSTokenInputHiddenInputs
      :name="props.name"
      :serialized-value="props.serializedValue"
      :tokens="tokens"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, useId, useTemplateRef } from "vue";
import DSBaseInput from "@/src/design_system/form_elements/base_input/components/DSBaseInput.vue";
import DSTokenInputChips from "@/src/design_system/form_elements/token_input/components/DSTokenInputChips.vue";
import DSTokenInputDraft from "@/src/design_system/form_elements/token_input/components/DSTokenInputDraft.vue";
import DSTokenInputHiddenInputs from "@/src/design_system/form_elements/token_input/components/DSTokenInputHiddenInputs.vue";
import { DS_TOKEN_INPUT_CLASSES } from "@/src/design_system/form_elements/token_input/constants/classes";
import { useTokenInputActions } from "@/src/design_system/form_elements/token_input/composables/useTokenInputActions";
import { useTokenInputCaret } from "@/src/design_system/form_elements/token_input/composables/useTokenInputCaret";
import { useTokenInputDraftSize } from "@/src/design_system/form_elements/token_input/composables/useTokenInputDraftSize";
import { useTokenInputGapClick } from "@/src/design_system/form_elements/token_input/composables/useTokenInputGapClick";
import { useTokenInputLiveRegion } from "@/src/design_system/form_elements/token_input/composables/useTokenInputLiveRegion";
import { useTokenInputKeyboard } from "@/src/design_system/form_elements/token_input/composables/useTokenInputKeyboard";
import { tokenChipId } from "@/src/design_system/form_elements/token_input/utils/tokenChipId";
import {
  DS_TOKEN_INPUT_BACKSPACE,
  type DSTokenInputEmits,
  type DSTokenInputProps,
} from "@/src/design_system/form_elements/token_input/types";

const props = withDefaults(defineProps<DSTokenInputProps>(), {
  modelValue: "",
  tokens: () => [],
  backspace: DS_TOKEN_INPUT_BACKSPACE.Select,
  commitKeys: () => ["Enter"],
  commitOnBlur: true,
  clearable: true,
  ariaExpanded: undefined,
});

const emit = defineEmits<DSTokenInputEmits>();

const baseInput = useTemplateRef<InstanceType<typeof DSBaseInput>>("baseInput");
const draft = useTemplateRef<InstanceType<typeof DSTokenInputDraft>>("draft");

const tokens = computed(() => props.tokens);
const isInactive = computed(() => props.disabled || props.readonly);
const placeholder = computed(() => (tokens.value.length ? undefined : props.placeholder));
const textarea = computed(() => draft.value?.textarea ?? null);
const wrapper = computed(() => textarea.value?.parentElement ?? null);

const focusDraft = async (position?: number | "end"): Promise<void> => {
  await nextTick();
  draft.value?.focus(position);
};

const caretApi = useTokenInputCaret({ tokens, caret: () => props.caret, emit });
const { announcement, announceRemoved, expectAddedToken } = useTokenInputLiveRegion({
  tokens,
  selectedIndex: caretApi.selectedIndex,
});
const actions = useTokenInputActions({
  caret: caretApi,
  tokens,
  draft: () => props.modelValue,
  isInactive,
  commitOnBlur: () => props.commitOnBlur,
  focusDraft,
  announceRemoved,
  expectAddedToken,
  emit,
});
const { caret, selectedIndex } = actions;

const { onKeydown } = useTokenInputKeyboard({
  actions,
  tokens,
  draft: () => props.modelValue,
  backspace: () => props.backspace,
  commitKeys: () => props.commitKeys,
  isInactive,
  commit: actions.commitDraft,
});

useTokenInputDraftSize({
  textarea,
  mirror: computed(() => draft.value?.mirror ?? null),
  wrapper,
  text: () => props.modelValue || placeholder.value || "",
});

const idPrefix = useId();
const draftDescribedby = computed(() => {
  const selected = selectedIndex.value;
  const ids = [props.ariaDescribedby, selected === null ? undefined : tokenChipId(idPrefix, selected)];

  return ids.filter(Boolean).join(" ") || undefined;
});

const { onFieldClick } = useTokenInputGapClick({
  wrapper,
  isInactive,
  deselect: () => actions.select(null),
  moveCaret: actions.moveCaret,
});

const onDraftInput = (event: Event, onInput: (event: Event) => void): void => {
  actions.select(null);
  onInput(event);
  emit("input", event);
};

const onDraftKeydown = (event: KeyboardEvent): void => {
  onKeydown(event);
  emit("keydown", event);
};

const onBlur = (event: FocusEvent): void => {
  actions.commitDraftOnBlur();
  emit("blur", event);
};

defineExpose({
  focus: (): void => baseInput.value?.focus(),
  blur: (): void => baseInput.value?.blur(),
  getInputElement: (): HTMLTextAreaElement | null => textarea.value,
  moveCaret: actions.moveCaret,
  selectToken: actions.select,
});
</script>
